import React, { useState, useEffect, useMemo, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { PermissionContext } from '@/contexts/PermissionContext';
import { api } from '@/lib/api';
import { formatCurrency, getProjectDuration, calculateWeeks, generateMonthlyInvoices } from '@/lib/dateUtils';
import { PROJECT_STATUSES, STATUS_COLORS, PROJECT_TYPES, CATEGORIES, INVOICE_STATUSES } from '@/lib/constants';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { PlusCircle, Trash2, Edit, FileText, DollarSign, Clock, FolderOpen, BarChart3, AlertCircle, User, Filter, CalendarRange, TrendingUp, Grid3X3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { MultiSelectFilter } from '@/components/MultiSelectFilter';
import { MonthlyConsumptionSlide } from '@/components/MonthlyConsumptionSlide';
import { ActualVsPlanned } from '@/components/ActualVsPlanned';
import { ResourceHeatmap } from '@/components/ResourceHeatmap';

const INVOICE_ELIGIBLE = ['sow', 'execute'];

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [adhocInvoices, setAdhocInvoices] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const defaultTab = location.state?.tab || 'projects';
  const { currentUserEmail } = useContext(PermissionContext);

  useEffect(() => {
    refreshProjects();
  }, []);

  const refreshProjects = () => {
    setLoading(true);
    Promise.all([
      api.getProjects(), // Already filters by user role on backend (creator, project lead, team member)
      api.getAdhocInvoices()
    ])
      .then(([projs, adhocs]) => { 
        setProjects(projs); 
        setAdhocInvoices(adhocs); 
        setLoading(false); 
      })
      .catch(() => { setProjects([]); setAdhocInvoices([]); setLoading(false); });
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteProject(id);
      const updated = await api.getProjects();
      setProjects(updated);
    } catch {
      // ignore
    }
  };

  const getStatusLabel = (val) => {
    const s = PROJECT_STATUSES.find(s => s.value === val);
    return s ? s.label : val;
  };

  const getTotalEstimate = (project) => {
    return (project.resources || []).reduce((sum, res) => {
      const resTotal = (res.weeklyAllocation || []).reduce((s, v) => s + (v || 0), 0) * (res.rate || 0);
      return sum + resTotal;
    }, 0);
  };

  const grandTotal = projects.reduce((sum, project) => sum + getTotalEstimate(project), 0);

  // Unique client list
  const clients = useMemo(() => {
    const names = [...new Set(projects.map(p => p.clientName).filter(Boolean))].sort();
    return names.map(n => ({ value: n, label: n }));
  }, [projects]);

  // Status options
  const statusOptions = useMemo(() => {
    return PROJECT_STATUSES.map(s => ({
      value: s.value,
      label: s.label,
      badge: STATUS_COLORS[s.value],
      badgeText: s.label,
    }));
  }, []);

  // Filtered projects by client + status
  const filteredProjects = useMemo(() => {
    return projects.filter(p => {
      if (selectedClients.length > 0 && !selectedClients.includes(p.clientName)) return false;
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(p.status)) return false;
      return true;
    });
  }, [projects, selectedClients, selectedStatuses]);

  const filteredGrandTotal = useMemo(() => {
    return filteredProjects.reduce((sum, p) => sum + getTotalEstimate(p), 0);
  }, [filteredProjects]);

  // Monthly consumption chart data (filtered, only sow/execute)
  const chartData = useMemo(() => {
    const monthMap = {};

    filteredProjects.forEach(p => {
      if (!p.startDate || !p.endDate) return;
      if (!INVOICE_ELIGIBLE.includes(p.status)) return;
      const numWeeks = calculateWeeks(p.startDate, p.endDate);
      const generated = generateMonthlyInvoices(p.startDate, p.endDate, p.resources || [], numWeeks);

      generated.forEach(inv => {
        const saved = (p.invoices || []).find(si => si.monthKey === inv.monthKey);
        const consumption = saved?.consumptionAmount !== undefined ? saved.consumptionAmount : inv.consumptionAmount;
        const invoiceVal = saved?.invoiceValue !== undefined ? saved.invoiceValue : inv.invoiceValue;

        if (!monthMap[inv.monthKey]) {
          monthMap[inv.monthKey] = { month: inv.month, monthKey: inv.monthKey, consumption: 0, invoiced: 0 };
        }
        monthMap[inv.monthKey].consumption += consumption;
        monthMap[inv.monthKey].invoiced += invoiceVal;
      });
    });

    // Add ad-hoc invoice amounts (filtered by client if needed)
    adhocInvoices.forEach(inv => {
      if (selectedClients.length > 0 && !selectedClients.includes(inv.clientName)) return;
      if (!monthMap[inv.monthKey]) {
        monthMap[inv.monthKey] = { month: inv.month, monthKey: inv.monthKey, consumption: 0, invoiced: 0 };
      }
      monthMap[inv.monthKey].consumption += (inv.consumptionAmount || inv.invoiceValue || 0);
      monthMap[inv.monthKey].invoiced += (inv.invoiceValue || 0);
    });

    return Object.values(monthMap).sort((a, b) => a.monthKey.localeCompare(b.monthKey));
  }, [filteredProjects, adhocInvoices, selectedClients]);

  // PO pending projects (filtered, only sow/execute)
  const poPendingProjects = useMemo(() => {
    const pending = [];
    filteredProjects.forEach(p => {
      if (!INVOICE_ELIGIBLE.includes(p.status)) return;
      const allInvs = [];
      if (p.startDate && p.endDate) {
        const numWeeks = calculateWeeks(p.startDate, p.endDate);
        const generated = generateMonthlyInvoices(p.startDate, p.endDate, p.resources || [], numWeeks);
        generated.forEach(gen => {
          const saved = (p.invoices || []).find(si => si.monthKey === gen.monthKey);
          allInvs.push(saved || gen);
        });
      }
      const hasPending = allInvs.some(inv => inv.status === 'po_awaited');
      if (hasPending) {
        const pendingMonths = allInvs.filter(inv => inv.status === 'po_awaited').map(inv => inv.month);
        pending.push({
          id: p.id,
          projectName: p.projectName || 'Untitled',
          clientName: p.clientName || 'No client',
          projectLead: p.projectLead || 'Not assigned',
          status: p.status,
          pendingMonths,
          totalEstimate: getTotalEstimate(p),
        });
      }
    });
    return pending;
  }, [filteredProjects]);

  // Timeline / Gantt data
  const timelineData = useMemo(() => {
    const projectsWithDates = filteredProjects.filter(p => p.startDate && p.endDate);
    if (projectsWithDates.length === 0) return { projects: [], months: [], minDate: null, maxDate: null };

    // Find global min/max dates
    let minDate = new Date(projectsWithDates[0].startDate);
    let maxDate = new Date(projectsWithDates[0].endDate);
    projectsWithDates.forEach(p => {
      const s = new Date(p.startDate);
      const e = new Date(p.endDate);
      if (s < minDate) minDate = s;
      if (e > maxDate) maxDate = e;
    });

    // Extend range: start at first of minDate month, end at last of maxDate month
    const rangeStart = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    const rangeEnd = new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 0);
    const totalDays = Math.max(1, (rangeEnd - rangeStart) / (1000 * 60 * 60 * 24));

    // Generate month labels
    const months = [];
    let cursor = new Date(rangeStart);
    while (cursor <= rangeEnd) {
      const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
      const startOffset = ((cursor - rangeStart) / (1000 * 60 * 60 * 24)) / totalDays * 100;
      const monthDays = (Math.min(monthEnd, rangeEnd) - cursor) / (1000 * 60 * 60 * 24);
      const widthPct = (monthDays / totalDays) * 100;
      months.push({
        label: cursor.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        fullLabel: cursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        left: startOffset,
        width: widthPct,
      });
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    }

    // Compute overlap count per day bucket (for congestion heatmap)
    const dayBuckets = new Array(Math.ceil(totalDays) + 1).fill(0);
    projectsWithDates.forEach(p => {
      const s = Math.max(0, Math.floor((new Date(p.startDate) - rangeStart) / (1000 * 60 * 60 * 24)));
      const e = Math.min(dayBuckets.length - 1, Math.ceil((new Date(p.endDate) - rangeStart) / (1000 * 60 * 60 * 24)));
      for (let i = s; i <= e; i++) dayBuckets[i]++;
    });
    const maxOverlap = Math.max(...dayBuckets, 1);

    // Compute congestion per month
    const monthCongestion = months.map(m => {
      const startDay = Math.round(m.left / 100 * totalDays);
      const endDay = Math.min(dayBuckets.length - 1, startDay + Math.round(m.width / 100 * totalDays));
      let sum = 0, count = 0;
      for (let i = startDay; i <= endDay; i++) { sum += dayBuckets[i]; count++; }
      return count > 0 ? sum / count : 0;
    });

    const projectBars = projectsWithDates.map(p => {
      const s = new Date(p.startDate);
      const e = new Date(p.endDate);
      const left = ((s - rangeStart) / (1000 * 60 * 60 * 24)) / totalDays * 100;
      const width = ((e - s) / (1000 * 60 * 60 * 24)) / totalDays * 100;
      const statusColor = STATUS_COLORS[p.status] || STATUS_COLORS.draft;
      return {
        id: p.id,
        name: p.projectName || 'Untitled',
        client: p.clientName || '',
        status: p.status,
        statusColor,
        left: Math.max(0, left),
        width: Math.max(0.5, width),
        startLabel: s.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        endLabel: e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        estimate: getTotalEstimate(p),
      };
    });

    return { projects: projectBars, months, monthCongestion, maxOverlap };
  }, [filteredProjects]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3 shadow-xl">
          <p className="font-manrope font-medium text-white text-sm mb-1">{label}</p>
          {payload.map((p, i) => (
            <p key={i} className="font-mono text-xs" style={{ color: p.color }}>
              {p.name}: {formatCurrency(p.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div data-testid="dashboard-page">
      {loading ? (
        <div className="space-y-6">
          {/* Header Skeleton */}
          <div className="flex items-center justify-between mb-6">
            <div className="space-y-3">
              <Skeleton className="h-10 w-64" />
              <Skeleton className="h-4 w-96" />
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-10 w-40" />
              <Skeleton className="h-10 w-40" />
            </div>
          </div>

          {/* Tabs Skeleton */}
          <div className="flex gap-2 mb-6">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-10 w-40" />
            <Skeleton className="h-10 w-40" />
          </div>

          {/* Grand Total Card Skeleton */}
          <Skeleton className="h-20 w-full rounded-lg mb-6" />

          {/* Project Cards Grid Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-zinc-950 border border-zinc-900 rounded-lg p-5 space-y-4">
                {/* Project ID */}
                <Skeleton className="h-3 w-24" />
                {/* Project Name */}
                <Skeleton className="h-5 w-full" />
                {/* Client Name */}
                <Skeleton className="h-4 w-48" />
                {/* Meta Info (dollar + duration) */}
                <div className="flex gap-4">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-32" />
                </div>
                {/* Action Buttons */}
                <div className="flex gap-2 pt-3 border-t border-zinc-900">
                  <Skeleton className="h-8 flex-1" />
                  <Skeleton className="h-8 flex-1" />
                  <Skeleton className="h-8 w-8" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
      <div className="flex flex-col gap-4 items-start md:items-center md:justify-between md:flex-row mb-6">
        <div>
          <h1 className="font-manrope font-bold text-2xl sm:text-3xl md:text-4xl tracking-tight text-white" data-testid="dashboard-title">
            Projects
          </h1>
          <p className="text-zinc-500 text-xs sm:text-sm mt-1 font-manrope">
            {filteredProjects.length} estimate{filteredProjects.length !== 1 ? 's' : ''}{selectedClients.length > 0 || selectedStatuses.length > 0 ? ' (filtered)' : ' total'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-zinc-500 flex-shrink-0" />
          <MultiSelectFilter
            label="Clients"
            options={clients}
            selected={selectedClients}
            onSelectionChange={setSelectedClients}
            testId="dashboard-client-filter"
          />
          <MultiSelectFilter
            label="Status"
            options={statusOptions}
            selected={selectedStatuses}
            onSelectionChange={setSelectedStatuses}
            testId="dashboard-status-filter"
          />
        </div>
      </div>

      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList className="bg-zinc-900 border border-zinc-800 mb-4 flex flex-wrap h-auto gap-1 p-1" data-testid="dashboard-tabs">
          <TabsTrigger value="projects" data-testid="tab-projects" className="data-[state=active]:bg-brand-500 data-[state=active]:text-white font-manrope text-xs sm:text-sm">
            Projects
          </TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline" className="data-[state=active]:bg-brand-500 data-[state=active]:text-white font-manrope text-xs sm:text-sm">
            <CalendarRange className="w-3.5 h-3.5 mr-1" />
            <span className="hidden sm:inline">Timeline</span>
          </TabsTrigger>
          <TabsTrigger value="po-pending" data-testid="tab-po-pending" className="data-[state=active]:bg-brand-500 data-[state=active]:text-white font-manrope text-xs sm:text-sm">
            <AlertCircle className="w-3.5 h-3.5 mr-1" />
            <span className="hidden sm:inline">PO</span>
          </TabsTrigger>
          <TabsTrigger value="actual-vs-planned" data-testid="tab-actual-vs-planned" className="data-[state=active]:bg-brand-500 data-[state=active]:text-white font-manrope text-xs sm:text-sm">
            <TrendingUp className="w-3.5 h-3.5 mr-1" />
            <span className="hidden sm:inline">Actual v/s</span>
          </TabsTrigger>
          <TabsTrigger value="resource-heatmap" data-testid="tab-resource-heatmap" className="data-[state=active]:bg-brand-500 data-[state=active]:text-white font-manrope text-xs sm:text-sm">
            <Grid3X3 className="w-3.5 h-3.5 mr-1" />
            <span className="hidden sm:inline">Heatmap</span>
          </TabsTrigger>
        </TabsList>

        {/* Projects Tab */}
        <TabsContent value="projects" data-testid="tab-content-projects">
          {filteredProjects.length > 0 && (
            <Card className="bg-zinc-950 border-zinc-900 mb-6" data-testid="grand-total-card">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-brand-400" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Total Revenue{selectedClients.length === 1 ? ` (${selectedClients[0]})` : ' (All Projects)'}</p>
                  <p className="font-mono text-xl font-bold text-brand-400" data-testid="dashboard-grand-total">{formatCurrency(filteredGrandTotal)}</p>
                </div>
              </CardContent>
            </Card>
          )}
          {filteredProjects.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="empty-state">
              <div className="w-16 h-16 rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-6">
                <FolderOpen className="w-8 h-8 text-zinc-600" />
              </div>
              <h2 className="font-manrope font-bold text-xl text-zinc-300 mb-2">No projects yet</h2>
              <p className="text-zinc-500 text-sm max-w-sm mb-6">
                Create your first project estimate to get started with tracking costs and invoices.
              </p>
              <Button
                data-testid="empty-create-btn"
                onClick={() => navigate('/estimate/new')}
                className="bg-brand-500 text-white hover:bg-brand-400 font-semibold"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                Create First Estimate
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-testid="projects-grid">
              {filteredProjects.map((project) => {
                const total = getTotalEstimate(project);
                const duration = getProjectDuration(project.startDate, project.endDate);
                const statusColor = STATUS_COLORS[project.status] || STATUS_COLORS.draft;
                return (
                  <Card
                    key={project.id}
                    className="bg-zinc-950 border-zinc-900 hover:border-brand-500/50 hover:shadow-lg hover:shadow-brand-500/20 cursor-pointer transition-all duration-200 group"
                    data-testid={`project-card-${project.id}`}
                    onClick={() => navigate(`/estimate/${project.id}`)}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-mono text-xs text-brand-500 tracking-wider mb-1" data-testid={`project-id-${project.id}`}>
                            {project.id}
                          </p>
                          <h3 className="font-manrope font-bold text-white text-base leading-tight" data-testid={`project-name-${project.id}`}>
                            {project.projectName || 'Untitled Project'}
                          </h3>
                        </div>
                        <Badge className={`${statusColor} border text-xs rounded-full`} data-testid={`project-status-${project.id}`}>
                          {getStatusLabel(project.status)}
                        </Badge>
                      </div>

                      <p className="text-zinc-500 text-sm mb-4 font-manrope" data-testid={`project-client-${project.id}`}>
                        {project.clientName || 'No client'}
                      </p>

                      <div className="flex items-center gap-4 mb-4 text-xs text-zinc-400">
                        <div className="flex items-center gap-1.5">
                          <DollarSign className="w-3.5 h-3.5 text-brand-500" />
                          <span className="font-mono" data-testid={`project-total-${project.id}`}>{formatCurrency(total)}</span>
                        </div>
                        {duration && (
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-zinc-500" />
                            <span>{duration}</span>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2 pt-3 border-t border-zinc-900">
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`edit-project-${project.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/estimate/${project.id}`);
                          }}
                          className="text-zinc-400 hover:text-white hover:bg-zinc-900 text-xs flex-1"
                        >
                          <Edit className="w-3.5 h-3.5 mr-1.5" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`view-invoices-${project.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/invoices/${project.id}`);
                          }}
                          className="text-brand-400 hover:text-brand-300 hover:bg-brand-500/10 text-xs flex-1"
                        >
                          <FileText className="w-3.5 h-3.5 mr-1.5" />
                          Invoices
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`delete-project-${project.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(project.id);
                          }}
                          className="text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* PO Pending Tab */}
        <TabsContent value="po-pending" data-testid="tab-content-po-pending">
          {poPendingProjects.length === 0 ? (
            <Card className="bg-zinc-950 border-zinc-900">
              <CardContent className="p-12 text-center">
                <AlertCircle className="w-10 h-10 mx-auto mb-4 text-zinc-700" />
                <h3 className="font-manrope font-bold text-lg text-zinc-300 mb-2">No pending POs</h3>
                <p className="text-zinc-500 text-sm">All project invoices have PO numbers assigned.</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-zinc-950 border-zinc-900" data-testid="po-pending-card">
              <CardContent className="p-0">
                <div className="p-4 border-b border-zinc-900">
                  <h3 className="font-manrope font-bold text-base text-white">Projects with Pending PO</h3>
                  <p className="text-zinc-500 text-xs mt-0.5">{poPendingProjects.length} project{poPendingProjects.length !== 1 ? 's' : ''} need PO follow-up</p>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Project ID</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Project Name</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Client</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Project Lead</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Estimate</TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Pending Months</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {poPendingProjects.map((p) => (
                      <TableRow key={p.id} className="border-zinc-900 hover:bg-zinc-900/30" data-testid={`po-pending-row-${p.id}`}>
                        <TableCell className="font-mono text-xs text-brand-500">{p.id}</TableCell>
                        <TableCell>
                          <button
                            onClick={() => navigate(`/invoices/${p.id}`)}
                            className="font-manrope text-sm text-brand-400 hover:text-brand-300 hover:underline"
                            data-testid={`po-pending-link-${p.id}`}
                          >
                            {p.projectName}
                          </button>
                        </TableCell>
                        <TableCell className="font-manrope text-sm text-zinc-300">{p.clientName}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <User className="w-3.5 h-3.5 text-zinc-500" />
                            <span className="font-manrope text-sm text-white">{p.projectLead}</span>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm text-zinc-300">{formatCurrency(p.totalEstimate)}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {p.pendingMonths.map((m, i) => (
                              <Badge key={i} className="text-amber-400 bg-amber-400/10 border-amber-400/20 border text-xs rounded-full">
                                {m}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Timeline View Tab */}
        <TabsContent value="timeline" data-testid="tab-content-timeline">
          {timelineData.projects.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <CalendarRange className="w-10 h-10 text-zinc-600 mb-4" />
              <p className="text-zinc-500 text-sm">No projects with dates to display on the timeline.</p>
            </div>
          ) : (
            <Card className="bg-zinc-950 border-zinc-900" data-testid="timeline-card">
              <CardContent className="p-0">
                <div className="p-4 border-b border-zinc-900 flex items-center justify-between">
                  <div>
                    <h3 className="font-manrope font-bold text-base text-white">Project Timeline</h3>
                    <p className="text-zinc-500 text-xs mt-0.5">{timelineData.projects.length} project{timelineData.projects.length !== 1 ? 's' : ''} shown</p>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-zinc-500">
                    <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-500/20 border border-emerald-500/30" /> Lean</span>
                    <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-500/30 border border-amber-500/40" /> Moderate</span>
                    <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-red-500/30 border border-red-500/40" /> Congested</span>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <div className="min-w-[800px]">
                    {/* Month header with congestion heatmap */}
                    <div className="flex border-b border-zinc-900">
                      <div className="w-52 shrink-0 px-4 py-2 border-r border-zinc-900">
                        <span className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Project</span>
                      </div>
                      <div className="flex-1 relative h-10">
                        {timelineData.months.map((m, i) => {
                          const cong = timelineData.monthCongestion[i] || 0;
                          const ratio = cong / timelineData.maxOverlap;
                          const bgColor = ratio < 0.33
                            ? 'bg-emerald-500/10 border-emerald-500/20'
                            : ratio < 0.66
                            ? 'bg-amber-500/10 border-amber-500/20'
                            : 'bg-red-500/10 border-red-500/20';
                          const textColor = ratio < 0.33
                            ? 'text-emerald-400'
                            : ratio < 0.66
                            ? 'text-amber-400'
                            : 'text-red-400';
                          return (
                            <div
                              key={i}
                              className={`absolute top-0 h-full border-r border-zinc-900/50 flex items-center justify-center ${bgColor}`}
                              style={{ left: `${m.left}%`, width: `${m.width}%` }}
                              title={`${m.fullLabel} — ${Math.round(cong)} avg concurrent projects`}
                            >
                              <span className={`text-[10px] font-mono font-medium ${textColor} truncate px-1`}>{m.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Project rows */}
                    {timelineData.projects.map((p, idx) => (
                      <div key={p.id} className="flex border-b border-zinc-900/50 hover:bg-zinc-900/20 transition-colors" data-testid={`timeline-row-${p.id}`}>
                        <div className="w-52 shrink-0 px-4 py-3 border-r border-zinc-900 flex flex-col justify-center">
                          <button
                            onClick={() => navigate(`/invoices/${p.id}`)}
                            className="font-manrope text-sm text-brand-400 hover:text-brand-300 hover:underline text-left truncate"
                            data-testid={`timeline-project-link-${p.id}`}
                          >
                            {p.name}
                          </button>
                          <span className="text-[10px] text-zinc-500 font-mono">{p.id} — {p.client}</span>
                        </div>
                        <div className="flex-1 relative py-2" style={{ minHeight: '44px' }}>
                          {/* Background month grid lines */}
                          {timelineData.months.map((m, mi) => (
                            <div
                              key={mi}
                              className="absolute top-0 h-full border-r border-zinc-900/30"
                              style={{ left: `${m.left}%`, width: `${m.width}%` }}
                            />
                          ))}
                          {/* Project bar */}
                          <button
                            onClick={() => navigate(`/invoices/${p.id}`)}
                            className="absolute top-1/2 -translate-y-1/2 h-7 rounded-md cursor-pointer group transition-all duration-200 hover:brightness-125 hover:scale-y-110"
                            style={{
                              left: `${p.left}%`,
                              width: `${p.width}%`,
                              minWidth: '8px',
                              background: `linear-gradient(135deg, rgba(168,0,151,0.7) 0%, rgba(168,0,151,0.35) 100%)`,
                              border: '1px solid rgba(168,0,151,0.5)',
                              boxShadow: '0 0 12px -3px rgba(168,0,151,0.3)',
                            }}
                            data-testid={`timeline-bar-${p.id}`}
                            title={`${p.name} | ${p.startLabel} → ${p.endLabel} | ${formatCurrency(p.estimate)}`}
                          >
                            <span className="absolute inset-0 flex items-center px-2 overflow-hidden">
                              <span className="text-[10px] font-mono font-semibold text-white truncate drop-shadow-md">
                                {p.width > 8 ? p.name : ''}
                              </span>
                            </span>
                            {/* Hover tooltip */}
                            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50">
                              <span className="bg-zinc-900 border border-zinc-700 text-white text-xs rounded-lg px-3 py-2 shadow-xl whitespace-nowrap font-manrope block">
                                <span className="font-semibold text-brand-400">{p.name}</span>
                                <br />
                                <span className="text-zinc-400">{p.startLabel} → {p.endLabel}</span>
                                <br />
                                <span className="text-zinc-300 font-mono">{formatCurrency(p.estimate)}</span>
                              </span>
                            </span>
                          </button>
                        </div>
                      </div>
                    ))}

                    {/* Congestion summary row */}
                    <div className="flex border-t border-zinc-800">
                      <div className="w-52 shrink-0 px-4 py-2 border-r border-zinc-900">
                        <span className="text-xs uppercase tracking-widest text-zinc-500 font-manrope font-semibold">Congestion</span>
                      </div>
                      <div className="flex-1 relative h-8">
                        {timelineData.months.map((m, i) => {
                          const cong = timelineData.monthCongestion[i] || 0;
                          const ratio = cong / timelineData.maxOverlap;
                          const color = ratio < 0.33 ? 'text-emerald-400' : ratio < 0.66 ? 'text-amber-400' : 'text-red-400';
                          return (
                            <div
                              key={i}
                              className="absolute top-0 h-full border-r border-zinc-900/50 flex items-center justify-center"
                              style={{ left: `${m.left}%`, width: `${m.width}%` }}
                            >
                              <span className={`text-xs font-mono font-bold ${color}`}>{Math.round(cong)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Actual vs Planned Tab */}
        <TabsContent value="actual-vs-planned" data-testid="tab-content-actual-vs-planned">
          <ActualVsPlanned projects={filteredProjects} onProjectsRefresh={refreshProjects} />
        </TabsContent>

        {/* Resource Heatmap Tab */}
        <TabsContent value="resource-heatmap" data-testid="tab-content-resource-heatmap">
          <ResourceHeatmap projects={filteredProjects} />
        </TabsContent>
      </Tabs>
        </>
      )}
    </div>
  );
}
